import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield,
  FileText,
  FolderKanban,
  ExternalLink,
  ChevronRight,
  ChevronLeft,
  Plus,
  ShoppingCart,
  HelpCircle,
  CheckCircle2,
  XCircle,
  X,
  Paperclip
} from 'lucide-react';
import { useProjectStore } from '../../lib/stores/projectStore';
import { useDealStore } from '../../lib/stores/dealStore';
import { useDocumentStore } from '../../lib/stores/documentStore';
import { ClientAuthService, type ClientPortalAccess } from '../../lib/services/clientAuthService';
import { ClientPortalAccessManager } from './ClientPortalAccessManager';
import { NotionDrawer } from '../ui/notion/NotionDrawer';
import { ClientOrderWorkflow } from './ClientOrderWorkflow';
import DocumentCollectionsSection from '../documents/DocumentCollectionsSection';
import { SignedUrl } from '../shared/SignedUrl';
import { supabase } from '../../lib/supabase';
import type { ClientQuizResult } from '../../lib/types/survey';
import { ClientSurveysSection } from './ClientSurveysSection';

interface ClientSidePanelProps {
  clientId: string;
  clientName: string;
  onCreateDeal?: () => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  isModal?: boolean;
}

// Notion-style section header
const SectionHeader = ({
  title,
  count,
  action
}: {
  title: string;
  count?: number;
  action?: { label: string; onClick: () => void }
}) => (
  <div className="flex items-center justify-between py-2 mb-1">
    <div className="flex items-center space-x-2">
      <h3 className="text-xs font-medium text-[#787774] dark:text-gray-400 uppercase tracking-wide">{title}</h3>
      {count !== undefined && (
        <span className="text-xs text-[#787774] dark:text-gray-500">({count})</span>
      )}
    </div>
    {action && (
      <button
        onClick={action.onClick}
        className="text-xs text-[#337EA9] dark:text-[#529CCA] hover:underline flex items-center gap-0.5"
        type="button"
      >
        <Plus className="h-3 w-3" />
        {action.label}
      </button>
    )}
  </div>
);

// Notion-style divider
const Divider = () => (
  <div className="border-b border-[rgba(55,53,47,0.09)] dark:border-gray-700 my-4" />
);

// Status class helpers
const getDealStatusClass = (status: string) => {
  switch (status) {
    case 'accepted':
      return 'text-[#0F7B6C] bg-[#DBEDDB] dark:bg-[#2B4B3F] dark:text-[#6BC9B5]';
    case 'rejected':
      return 'text-[#E16259] bg-[#FFE2DD] dark:bg-[#5C2B29] dark:text-[#E57B78]';
    case 'sent':
      return 'text-[#337EA9] bg-[#D3E5EF] dark:bg-[#294350] dark:text-[#529CCA]';
    case 'draft':
      return 'text-[#CB912F] bg-[#FBF3DB] dark:bg-[#59563B] dark:text-[#FFDC49]';
    default:
      return 'text-[#787774] bg-[#F1F1F0] dark:bg-gray-700 dark:text-gray-400';
  }
};

const getDealStatusLabel = (status: string) => {
  switch (status) {
    case 'draft': return 'Bozza';
    case 'sent': return 'Inviata';
    case 'accepted': return 'Accettata';
    case 'rejected': return 'Rifiutata';
    default: return status;
  }
};

const getProjectStatusClass = (status: string) => {
  switch (status) {
    case 'active':
    case 'in_progress':
      return 'text-[#337EA9] bg-[#D3E5EF] dark:bg-[#294350] dark:text-[#529CCA]';
    case 'completed':
      return 'text-[#0F7B6C] bg-[#DBEDDB] dark:bg-[#2B4B3F] dark:text-[#6BC9B5]';
    case 'on_hold':
    case 'paused':
      return 'text-[#CB912F] bg-[#FBF3DB] dark:bg-[#59563B] dark:text-[#FFDC49]';
    case 'cancelled':
      return 'text-[#E16259] bg-[#FFE2DD] dark:bg-[#5C2B29] dark:text-[#E57B78]';
    default:
      return 'text-[#787774] bg-[#F1F1F0] dark:bg-gray-700 dark:text-gray-400';
  }
};

const getProjectStatusLabel = (status: string) => {
  switch (status) {
    case 'active':
    case 'in_progress': return 'Attivo';
    case 'completed': return 'Completato';
    case 'on_hold':
    case 'paused': return 'In pausa';
    case 'cancelled': return 'Annullato';
    case 'planning': return 'Pianificazione';
    default: return status;
  }
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('it-IT', {
    day: '2-digit',
    month: 'short'
  });
};

export function ClientSidePanel({ clientId, clientName, onCreateDeal, isCollapsed = false, onToggleCollapse, isModal = false }: ClientSidePanelProps) {
  const navigate = useNavigate();
  const { projects, fetchProjects } = useProjectStore();
  const { deals, fetchDeals } = useDealStore();
  const { documents, fetchClientDocuments } = useDocumentStore();

  const [portalAccesses, setPortalAccesses] = useState<ClientPortalAccess[]>([]);
  const [portalLoading, setPortalLoading] = useState(true);
  const [showPortalModal, setShowPortalModal] = useState(false);
  const [quizResults, setQuizResults] = useState<ClientQuizResult[]>([]);
  const [selectedQuiz, setSelectedQuiz] = useState<ClientQuizResult | null>(null);

  // Fetch data on mount. In modal mode (drawer del lead/cliente) il dealStore
  // potrebbe essere vuoto se l'utente non ha mai visitato /deals in questa sessione:
  // il count "0 offerte" appariva sul pannello laterale anche quando il lead
  // aveva offerte reali in DB. Forziamo il fetch lazy quando lo store e' vuoto,
  // anche in modal, così le offerte appaiono subito.
  useEffect(() => {
    loadPortalAccess();
    if (isModal) {
      // In modal mode fetcha solo se gli store sono vuoti (evita refresh inutili)
      if (deals.length === 0) fetchDeals();
      if (projects.length === 0) fetchProjects();
      return;
    }
    fetchProjects();
    fetchDeals();
  }, [clientId, isModal]);

  // Fetch documents
  useEffect(() => {
    if (clientId) fetchClientDocuments(clientId);
  }, [clientId]);

  // Fetch quiz results
  useEffect(() => {
    if (!clientId) return;
    supabase
      .from('client_quiz_results')
      .select('*')
      .eq('client_id', clientId)
      .order('completed_at', { ascending: false })
      .then(({ data }) => setQuizResults(data || []));
  }, [clientId]);

  const loadPortalAccess = async () => {
    setPortalLoading(true);
    try {
      const accesses = await ClientAuthService.getClientAccessList(clientId);
      setPortalAccesses(accesses);
    } catch (error) {
      console.error('Error loading portal access:', error);
    } finally {
      setPortalLoading(false);
    }
  };

  // Filter data for this client
  const clientProjects = projects.filter(p => p.client_id === clientId);
  const clientDeals = deals.filter(d => d.client_id === clientId);

  // Check if portal is configured
  const hasActivePortalAccess = portalAccesses.some(a => a.is_active);

  // Calculate project progress. I progetti che arrivano dalla lista portano solo i task
  // non completati: totale e completati dagli aggregati, con fallback sull'array per i
  // progetti caricati altrove (dettaglio progetto).
  const getProjectProgress = (project: any) => {
    const tasks = project.tasks || [];
    const total = project.task_stats?.total_all ?? tasks.length;
    const completed = project.task_stats?.completed ?? tasks.filter((t: any) => t.status === 'completed').length;
    return { total, completed, percentage: total > 0 ? Math.round((completed / total) * 100) : 0 };
  };

  // Calculate total deals value
  const totalDealsValue = clientDeals.reduce((sum, d) => sum + (d.amount || 0), 0);

  // Collapsed view - only show expand button with icons
  if (isCollapsed) {
    return (
      <div className="h-full flex flex-col items-center py-4 space-y-4">
        {/* Expand button */}
        <button
          onClick={onToggleCollapse}
          className="p-2 hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/[0.08] rounded transition-colors"
          title="Espandi pannello"
        >
          <ChevronLeft className="h-4 w-4 text-[#787774] dark:text-gray-400" />
        </button>

        {/* Deals icon with count */}
        <div
          className="flex flex-col items-center gap-1 text-[#787774] dark:text-gray-400 cursor-pointer hover:text-[#37352F] dark:hover:text-white transition-colors"
          onClick={onToggleCollapse}
          title={`${clientDeals.length} offerte - €${totalDealsValue.toLocaleString('it-IT')}`}
        >
          <FileText className="h-4 w-4" />
          <span className="text-xs font-medium">{clientDeals.length}</span>
        </div>

        {/* Orders icon */}
        <div
          className="flex flex-col items-center gap-1 text-[#787774] dark:text-gray-400 cursor-pointer hover:text-[#37352F] dark:hover:text-white transition-colors"
          onClick={onToggleCollapse}
          title="Ordini cliente"
        >
          <ShoppingCart className="h-4 w-4" />
        </div>

        {/* Projects icon with count */}
        <div
          className="flex flex-col items-center gap-1 text-[#787774] dark:text-gray-400 cursor-pointer hover:text-[#37352F] dark:hover:text-white transition-colors"
          onClick={onToggleCollapse}
          title={`${clientProjects.length} progetti`}
        >
          <FolderKanban className="h-4 w-4" />
          <span className="text-xs font-medium">{clientProjects.length}</span>
        </div>

        {/* Portal Manager Drawer - still needed even when collapsed */}
        <NotionDrawer
          isOpen={showPortalModal}
          onClose={() => {
            setShowPortalModal(false);
            loadPortalAccess();
          }}
          title="Portale Cliente"
          subtitle={`Gestisci l'accesso al portale per ${clientName}`}
          width="lg"
        >
          <ClientPortalAccessManager
            clientId={clientId}
            clientName={clientName}
          />
        </NotionDrawer>
      </div>
    );
  }

  // Expanded view
  return (
    <div className="h-full overflow-y-auto">
      {/* Header with collapse button */}
      <div className="flex items-center justify-end px-2 py-2 mb-2">
        <button
          onClick={onToggleCollapse}
          className="p-1 hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/[0.08] rounded transition-colors"
          title="Comprimi pannello"
        >
          <ChevronRight className="h-4 w-4 text-[#787774] dark:text-gray-400" />
        </button>
      </div>

      {/* Portal Section */}
          <div
            className="flex items-center justify-between py-2 px-2 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/[0.03] rounded transition-colors cursor-pointer"
            onClick={() => setShowPortalModal(true)}
          >
            <div className="flex items-center space-x-2.5">
              <Shield className="h-4 w-4 text-[#787774] dark:text-gray-500" />
              <span className="text-sm text-[#787774] dark:text-gray-400">Portale Cliente</span>
            </div>
            <div className="flex items-center gap-1.5">
              {portalLoading ? (
                <div className="w-12 h-4 bg-[rgba(55,53,47,0.08)] dark:bg-gray-700 rounded animate-pulse" />
              ) : hasActivePortalAccess ? (
                <span className="text-xs text-[#0F7B6C] bg-[#DBEDDB] dark:bg-[#2B4B3F] dark:text-[#6BC9B5] px-1.5 py-0.5 rounded">
                  Attivo
                </span>
              ) : (
                <span className="text-xs text-[#787774] bg-[#F1F1F0] dark:bg-gray-700 dark:text-gray-400 px-1.5 py-0.5 rounded">
                  Configura
                </span>
              )}
              <ChevronRight className="h-3.5 w-3.5 text-[#787774] dark:text-gray-500" />
            </div>
          </div>

          <Divider />

          {/* Deals Section */}
          <SectionHeader
            title="Offerte"
            count={clientDeals.length}
            action={onCreateDeal ? {
              label: 'Nuova',
              onClick: onCreateDeal
            } : undefined}
          />

          {clientDeals.length === 0 ? (
            <p className="py-2 px-2 text-sm text-[#787774] dark:text-gray-400 italic">
              Nessuna offerta
            </p>
          ) : (
            <div className="space-y-0.5">
              {clientDeals.slice(0, 5).map(deal => (
                <div
                  key={deal.id}
                  className="py-2 px-2 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/[0.03] rounded transition-colors cursor-pointer"
                  onClick={() => window.dispatchEvent(new CustomEvent('open-deal-drawer', { detail: { entityId: deal.id } }))}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2 min-w-0 flex-1">
                      <FileText className="h-4 w-4 text-[#787774] dark:text-gray-500 flex-shrink-0" />
                      <span className="text-sm text-[#37352F] dark:text-white truncate">
                        {deal.title}
                      </span>
                    </div>
                    <span className={`text-xs px-1.5 py-0.5 rounded flex-shrink-0 ml-2 ${getDealStatusClass(deal.status)}`}>
                      {getDealStatusLabel(deal.status)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between ml-6 mt-0.5">
                    <span className="text-xs text-[#787774] dark:text-gray-400">
                      €{(deal.amount || 0).toLocaleString('it-IT')}
                    </span>
                    <span className="text-xs text-[#787774] dark:text-gray-500">
                      {formatDate(deal.created_at)}
                    </span>
                  </div>
                </div>
              ))}
              {clientDeals.length > 5 && (
                <button
                  onClick={() => navigate(`/deals?client=${clientId}`)}
                  className="w-full py-2 px-2 text-xs text-[#337EA9] dark:text-[#529CCA] hover:underline flex items-center justify-center gap-1"
                >
                  Vedi tutte ({clientDeals.length})
                  <ExternalLink className="h-3 w-3" />
                </button>
              )}
            </div>
          )}

          <Divider />

          {/* Orders Section */}
          <ClientOrderWorkflow clientId={clientId} />

          <Divider />

          {/* Projects Section */}
          <SectionHeader
            title="Progetti"
            count={clientProjects.length}
          />

          {clientProjects.length === 0 ? (
            <p className="py-2 px-2 text-sm text-[#787774] dark:text-gray-400 italic">
              Nessun progetto
            </p>
          ) : (
            <div className="space-y-0.5">
              {clientProjects.slice(0, 5).map(project => {
                const progress = getProjectProgress(project);
                return (
                  <div
                    key={project.id}
                    className="py-2 px-2 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/[0.03] rounded transition-colors cursor-pointer"
                    onClick={() => navigate(`/projects/${project.id}`)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2 min-w-0 flex-1">
                        <FolderKanban className="h-4 w-4 text-[#787774] dark:text-gray-500 flex-shrink-0" />
                        <span className="text-sm text-[#37352F] dark:text-white truncate">
                          {project.name}
                        </span>
                      </div>
                      <span className={`text-xs px-1.5 py-0.5 rounded flex-shrink-0 ml-2 ${getProjectStatusClass(project.status)}`}>
                        {getProjectStatusLabel(project.status)}
                      </span>
                    </div>
                    {/* Progress bar */}
                    <div className="ml-6 mt-1.5">
                      <div className="flex items-center justify-between text-xs mb-0.5">
                        <span className="text-[#787774] dark:text-gray-500">
                          {progress.completed}/{progress.total} task
                        </span>
                        <span className="text-[#787774] dark:text-gray-500">
                          {progress.percentage}%
                        </span>
                      </div>
                      <div className="h-1 bg-[rgba(55,53,47,0.08)] dark:bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#35A5E4] rounded-full transition-all"
                          style={{ width: `${progress.percentage}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
              {clientProjects.length > 5 && (
                <button
                  onClick={() => navigate(`/projects?client=${clientId}`)}
                  className="w-full py-2 px-2 text-xs text-[#337EA9] dark:text-[#529CCA] hover:underline flex items-center justify-center gap-1"
                >
                  Vedi tutti ({clientProjects.length})
                  <ExternalLink className="h-3 w-3" />
                </button>
              )}
            </div>
          )}

          {/* Documenti richiesti: cosa aspettiamo ancora dal cliente */}
          <Divider />
          <DocumentCollectionsSection aggancio={{ client_id: clientId }} />

          {/* Documents Section */}
          {documents.length > 0 && (
            <>
              <Divider />
              <SectionHeader title="Documenti" count={documents.length} />
              <div className="space-y-0.5">
                {documents.map((doc) => (
                  /* I documenti stanno in un deposito privato: il link va firmato
                     al momento dell'apertura, altrimenti non si apre niente. */
                  <SignedUrl key={doc.id} url={doc.url}>
                    {(href) => (
                      <a
                        href={href ?? undefined}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block py-2 px-2 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/[0.03] rounded transition-colors"
                      >
                        <div className="flex items-center space-x-2 min-w-0">
                          <Paperclip className="h-4 w-4 text-[#787774] dark:text-gray-500 flex-shrink-0" />
                          <span className="text-sm text-[#37352F] dark:text-white truncate">
                            {doc.name}
                          </span>
                        </div>
                      </a>
                    )}
                  </SignedUrl>
                ))}
              </div>
            </>
          )}

          {/* Quiz Results Section */}
          {quizResults.length > 0 && (
            <>
              <Divider />
              <SectionHeader title="Quiz" count={quizResults.length} />
              <div className="space-y-0.5">
                {quizResults.map((qr) => (
                  <div
                    key={qr.id}
                    className="py-2 px-2 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/[0.03] rounded transition-colors cursor-pointer"
                    onClick={() => setSelectedQuiz(qr)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2 min-w-0 flex-1">
                        <HelpCircle className="h-4 w-4 text-[#787774] dark:text-gray-500 flex-shrink-0" />
                        <span className="text-sm text-[#37352F] dark:text-white truncate">
                          {qr.quiz_title || 'Quiz'}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                        <span className="text-xs font-medium text-[#37352F] dark:text-white">
                          {qr.score}/{qr.total}
                        </span>
                        {qr.passed ? (
                          <CheckCircle2 className="h-3.5 w-3.5 text-[#0F7B6C]" />
                        ) : (
                          <XCircle className="h-3.5 w-3.5 text-[#E16259]" />
                        )}
                      </div>
                    </div>
                    <div className="ml-6 mt-0.5 flex items-center gap-2">
                      <span className="text-xs text-[#787774] dark:text-gray-500">
                        {qr.form_id ? 'Form' : 'Sondaggio'} &middot; {formatDate(qr.completed_at)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Sondaggi: risposte del contatto + link personale */}
          <ClientSurveysSection clientId={clientId} />

      {/* Quiz Detail Modal */}
      {selectedQuiz && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setSelectedQuiz(null)} />
          <div className="relative bg-white dark:bg-gray-900 rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div>
                <h3 className="text-base font-semibold text-[#37352F] dark:text-white">
                  {selectedQuiz.quiz_title || 'Quiz'}
                </h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-sm text-[#787774] dark:text-gray-400">
                    {selectedQuiz.score}/{selectedQuiz.total} corrette
                  </span>
                  <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                    selectedQuiz.passed
                      ? 'text-[#0F7B6C] bg-[#DBEDDB] dark:bg-[#2B4B3F] dark:text-[#6BC9B5]'
                      : 'text-[#E16259] bg-[#FFE2DD] dark:bg-[#5C2B29] dark:text-[#E57B78]'
                  }`}>
                    {selectedQuiz.passed ? 'Superato' : 'Non superato'}
                  </span>
                  <span className="text-xs text-[#787774] dark:text-gray-500">
                    {formatDate(selectedQuiz.completed_at)}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setSelectedQuiz(null)}
                className="p-1.5 text-[#787774] hover:text-[#37352F] dark:hover:text-white rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Body — domande e risposte */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              {selectedQuiz.quiz_config?.questions?.length ? (
                selectedQuiz.quiz_config.questions.map((question, qIdx) => {
                  const answer = (selectedQuiz.answers || []).find(
                    (a: any) => a.questionId === question.id
                  );
                  const selectedIds = new Set(answer?.selectedOptionIds || []);

                  return (
                    <div key={question.id} className="space-y-2">
                      <div className="flex items-start gap-2">
                        <span className="text-xs font-bold text-[#787774] dark:text-gray-500 mt-0.5 flex-shrink-0">
                          {qIdx + 1}.
                        </span>
                        <p className="text-sm font-medium text-[#37352F] dark:text-white">
                          {question.text}
                        </p>
                        {answer && (
                          answer.correct
                            ? <CheckCircle2 className="h-4 w-4 text-[#0F7B6C] flex-shrink-0 mt-0.5" />
                            : <XCircle className="h-4 w-4 text-[#E16259] flex-shrink-0 mt-0.5" />
                        )}
                      </div>
                      <div className="ml-5 space-y-1">
                        {question.options.map((opt: any) => {
                          const wasSelected = selectedIds.has(opt.id);
                          const isCorrect = opt.isCorrect;
                          let bgClass = 'bg-gray-50 dark:bg-gray-800';
                          let borderClass = 'border-gray-200 dark:border-gray-700';
                          let textClass = 'text-[#787774] dark:text-gray-400';

                          if (wasSelected && isCorrect) {
                            bgClass = 'bg-[#DBEDDB] dark:bg-[#2B4B3F]';
                            borderClass = 'border-[#0F7B6C]/30';
                            textClass = 'text-[#0F7B6C] dark:text-[#6BC9B5]';
                          } else if (wasSelected && !isCorrect) {
                            bgClass = 'bg-[#FFE2DD] dark:bg-[#5C2B29]';
                            borderClass = 'border-[#E16259]/30';
                            textClass = 'text-[#E16259] dark:text-[#E57B78]';
                          } else if (!wasSelected && isCorrect) {
                            bgClass = 'bg-[#DBEDDB]/50 dark:bg-[#2B4B3F]/50';
                            borderClass = 'border-[#0F7B6C]/20 border-dashed';
                            textClass = 'text-[#0F7B6C]/70 dark:text-[#6BC9B5]/70';
                          }

                          return (
                            <div
                              key={opt.id}
                              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm ${bgClass} ${borderClass} ${textClass}`}
                            >
                              {wasSelected && isCorrect && <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" />}
                              {wasSelected && !isCorrect && <XCircle className="h-3.5 w-3.5 flex-shrink-0" />}
                              {!wasSelected && isCorrect && <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0 opacity-50" />}
                              {!wasSelected && !isCorrect && <span className="w-3.5" />}
                              <span>{opt.text}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-sm text-[#787774] dark:text-gray-400 italic">
                  Dettaglio domande non disponibile per questo quiz.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Portal Manager Drawer */}
      <NotionDrawer
        isOpen={showPortalModal}
        onClose={() => {
          setShowPortalModal(false);
          loadPortalAccess(); // Refresh after drawer closes
        }}
        title="Portale Cliente"
        subtitle={`Gestisci l'accesso al portale per ${clientName}`}
        width="lg"
      >
        <ClientPortalAccessManager
          clientId={clientId}
          clientName={clientName}
        />
      </NotionDrawer>

    </div>
  );
}
